import type {
  ClientPortfolioAssignmentView,
  DirectoryMember,
  ServiceRelationshipConfig,
  ServiceRelationshipView,
} from "@closer/shared";
import {
  PORTFOLIO_ASSIGNMENT_STATUS_NAMES,
  PORTFOLIO_CLOSED_REASON_NAMES,
  PORTFOLIO_PRIORITIES,
  PORTFOLIO_PRIORITY_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, FormField, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { UyeListesiDurumu } from "../../components/UyeListesiDurumu";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * PORTFÖY SORUMLULUĞU (CLOS-247 `[135]`).
 *
 * ### ⚠ Sorumluluk ERİŞİM DEĞİLDİR
 *
 * Bölüm bunu cümleyle yazıyor. Atama büro içi bir operasyon kaydı: kim bakıyor sorusunun
 * cevabı. Kayıt erişimi, temsil, onay ve imza yetkisi vermiyor — sunucu da bu tabloya
 * bakarak hiçbir kapı açmıyor.
 *
 * ### ⚠ Sorumlu değişikliği ÜZERİNE YAZMIYOR
 *
 * "Sorumluyu değiştir" eski atamayı kapatıp yeni satır açıyor; liste her iki satırı da
 * gösteriyor. Üzerine yazsaydık "bu müvekkile geçen ay kim bakıyordu" sorusunun cevabı
 * kaybolurdu.
 *
 * ### ⚠ İç not BÜRO İÇİ
 *
 * Alanın adı "iç not" ve bu bir uyarı: karşı tarafa açılan hiçbir görünümde yer almıyor.
 *
 * ### ⚠ Sorumluluk AKTİF hizmet ilişkisine bağlı
 *
 * Atama formu yalnız aktif ilişkileri sunuyor. Askıdaki/sona ermiş ilişkiyi de sunsaydık
 * kullanıcı, sunucunun reddedeceği bir atamayı denerdi.
 */
export function ClientPortfolioAssignments({ clientId }: { clientId: string }) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [iliskiSecimi, setIliskiSecimi] = useState("");
  const [kisiSecimi, setKisiSecimi] = useState("");
  const [oncelik, setOncelik] = useState<string>("normal");
  const [not, setNot] = useState("");
  const [devir, setDevir] = useState<Record<string, string>>({});

  const yetkili = can("client.portfolio");

  const anahtar = tenantKey(tenantId, "client-portfolio-assignments", clientId);
  const atamalar = useQuery({
    enabled: yetkili,
    queryKey: anahtar,
    queryFn: async () =>
      (
        await api<ClientPortfolioAssignmentView[]>(
          `/api/clients/${clientId}/portfolio-assignments`,
          { tenantId },
        )
      ).data,
  });

  /** ⚠ Yalnız AKTİF ilişki: sorumluluk açık bir hizmet ilişkisine tanımlanır. */
  const iliskiler = useQuery({
    enabled: yetkili && can("client.service_scope"),
    queryKey: tenantKey(tenantId, "client-service-relationships", clientId),
    queryFn: async () =>
      (
        await api<ServiceRelationshipView[]>(`/api/clients/${clientId}/service-relationships`, {
          tenantId,
        })
      ).data,
  });

  const politika = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "service-policy"),
    queryFn: async () =>
      (await api<ServiceRelationshipConfig>("/api/clients/service-policy", { tenantId })).data,
  });

  const uyeler = useQuery({
    enabled: yetkili,
    queryKey: tenantKey(tenantId, "members"),
    queryFn: async () =>
      (await api<{ members: DirectoryMember[] }>(`/api/tenants/${tenantId}/members`, { tenantId }))
        .data.members,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  const ata = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ClientPortfolioAssignmentView>(`/api/clients/${clientId}/portfolio-assignments`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async () => {
      setIliskiSecimi("");
      setKisiSecimi("");
      setNot("");
      await yenile();
      toast.success("Portföy sorumluluğu tanımlandı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sorumluluk tanımlanamadı"),
  });

  const devret = useMutation({
    mutationFn: (girdi: { paId: string; assigneeUserId: string }) =>
      api(`/api/clients/${clientId}/portfolio-assignments/${girdi.paId}/reassign`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ assigneeUserId: girdi.assigneeUserId }),
      }),
    onSuccess: async () => {
      setDevir({});
      await yenile();
      toast.success("Sorumlu değiştirildi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sorumlu değiştirilemedi"),
  });

  const kapat = useMutation({
    mutationFn: (paId: string) =>
      api(`/api/clients/${clientId}/portfolio-assignments/${paId}/close`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Sorumluluk kapatıldı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sorumluluk kapatılamadı"),
  });

  if (!yetkili) return null;

  const aktifIliskiler = (iliskiler.data ?? []).filter((i) => i.status === "active");
  const aktifUyeler = (uyeler.data ?? []).filter((u) => u.status === "active");
  const kapsamAdi = (deger: string) =>
    politika.data?.scopeTypes.find((k) => k.key === deger)?.label ?? deger;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <h2 className="text-base font-semibold text-fg-heading">Portföy sorumluluğu</h2>

        {/* ⚠ Sorumluluk ERİŞİM DEĞİL: kullanıcı bunu çıkarımla öğrenmiyor. */}
        <p className="text-xs text-fg-soft">
          Sorumluluk büro içi bir takip kaydıdır; kayıt erişimi, temsil, onay veya imza yetkisi
          vermez. İç not büro dışına çıkmaz.
        </p>

        {/*
          ⚠ ERİŞİMSİZLİK ≠ YOKLUK. Hizmet ilişkilerini göremeyen kullanıcıya "aktif ilişki yok"
          demek, olmayan bir gerçeği bildirmek olurdu: liste boş çünkü sorulmadı, sorulmadı
          çünkü yetki yok. İki durum iki ayrı cümleyle anlatılıyor.
        */}
        {!can("client.service_scope") ? (
          <p className="text-xs text-fg-soft">
            Sorumluluk tanımlamak için hizmet ilişkilerini görme yetkisi gerekiyor; bu yetki sizde
            olmadığı için ilişki listesi getirilmedi.
          </p>
        ) : aktifIliskiler.length === 0 ? (
          <p className="text-xs text-fg-soft">
            Portföy sorumluluğu yalnız aktif bir hizmet ilişkisine tanımlanır; aktif ilişki yok.
          </p>
        ) : (
          <div className="flex flex-col gap-2 rounded-md border border-border-default p-3">
            <FormField label="Hizmet ilişkisi">
              <Select
                id="portfoy-iliski"
                value={iliskiSecimi}
                onChange={(olay) => setIliskiSecimi(olay.target.value)}
              >
                <option value="">Seçiniz</option>
                {aktifIliskiler.map((i) => (
                  <option key={i.id} value={i.id}>
                    {kapsamAdi(i.scope)}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="Sorumlu">
              <Select
                id="portfoy-kisi"
                value={kisiSecimi}
                onChange={(olay) => setKisiSecimi(olay.target.value)}
              >
                <option value="">Seçiniz</option>
                {aktifUyeler.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </FormField>

            <UyeListesiDurumu hata={uyeler.error} bosMu={aktifUyeler.length === 0} />

            <FormField label="Öncelik">
              <Select
                id="portfoy-oncelik"
                value={oncelik}
                onChange={(olay) => setOncelik(olay.target.value)}
              >
                {PORTFOLIO_PRIORITIES.map((o) => (
                  <option key={o} value={o}>
                    {PORTFOLIO_PRIORITY_NAMES[o]}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="İç not" hint="Yalnız büro içinde görünür.">
              <Textarea
                id="portfoy-not"
                rows={2}
                value={not}
                onChange={(olay) => setNot(olay.target.value)}
              />
            </FormField>

            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                disabled={!iliskiSecimi || !kisiSecimi}
                loading={ata.isPending}
                onClick={() =>
                  ata.mutate({
                    serviceRelationshipId: iliskiSecimi,
                    assigneeUserId: kisiSecimi,
                    priority: oncelik,
                    ...(not.trim() ? { internalNote: not.trim() } : {}),
                  })
                }
              >
                Sorumlu ata
              </Button>
            </div>
          </div>
        )}

        {atamalar.isPending ? (
          <div role="status" aria-label="Portföy sorumlulukları yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : atamalar.error ? (
          <StateView
            state={resolveErrorState(atamalar.error, { idempotent: true })}
            onRetry={() => void atamalar.refetch()}
          />
        ) : (atamalar.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Bu müvekkil için portföy sorumlusu tanımlanmamış.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(atamalar.data ?? []).map((atama) => (
              <li
                key={atama.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-fg-strong">{atama.assigneeName}</span>
                  <Badge tone={atama.status === "active" ? "success" : "neutral"}>
                    {PORTFOLIO_ASSIGNMENT_STATUS_NAMES[atama.status]}
                  </Badge>
                  <span className="text-xs text-fg-soft">
                    {PORTFOLIO_PRIORITY_NAMES[atama.priority]}
                  </span>
                  {/* ⚠ Kapanış GEREKÇESİ görünür: devir mi, boşluk mu? */}
                  {atama.closedReason ? (
                    <span className="text-xs text-fg-soft">
                      {PORTFOLIO_CLOSED_REASON_NAMES[atama.closedReason]}
                    </span>
                  ) : null}
                </div>

                {atama.internalNote ? (
                  <p className="text-sm text-fg">{atama.internalNote}</p>
                ) : null}

                {/* ⚠ Kapanmış atamada komut YOK: geçmiş satır düzenlenmiyor. */}
                {atama.status === "active" ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <FormField label="Yeni sorumlu">
                      <Select
                        aria-label={`${atama.assigneeName} yerine`}
                        value={devir[atama.id] ?? ""}
                        onChange={(olay) =>
                          setDevir((onceki) => ({ ...onceki, [atama.id]: olay.target.value }))
                        }
                      >
                        <option value="">Seçiniz</option>
                        {aktifUyeler
                          .filter((u) => u.userId !== atama.assigneeUserId)
                          .map((u) => (
                            <option key={u.userId} value={u.userId}>
                              {u.name}
                            </option>
                          ))}
                      </Select>
                    </FormField>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!devir[atama.id]}
                      onClick={() =>
                        devret.mutate({ paId: atama.id, assigneeUserId: devir[atama.id]! })
                      }
                    >
                      Sorumluyu değiştir
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => kapat.mutate(atama.id)}
                    >
                      Sorumluluğu kapat
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
